import React, { useState, useEffect, useRef, useCallback } from 'react';
import { saveUserProfile, getUserProfile, requestDataPortability, getQuestGenerationStatus, getAiFeatureQuotas, deleteAccount, submitFitnessAssessment } from '../api/dataService';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    BookOpen,
    CalendarRange,
    Check,
    ChevronRight,
    Download,
    Dumbbell,
    Headset,
    HeartPulse,
    LogOut,
    MapPin,
    PencilLine,
    Ruler,
    Trash2,
    UserRound,
    Utensils,
    Weight
} from 'lucide-react';
import Modal from '../components/Modal';
import {
    AI_FEATURE_KEYS,
    getFeatureQuota,
    getQuotaMessage,
    getRewardedUnlock,
    isQuotaBlocked
} from '../utils/aiFeatureQuotas';
import {
    EXERCISE_FAMILIARITY_OPTIONS,
    FITNESS_ASSESSMENT_DOMAINS,
    FITNESS_ASSESSMENT_DIFFICULTY_STYLES,
    createInitialAssessmentAnswers,
    scoreFitnessAssessment
} from '../utils/fitnessAssessment';
import {
    ACTIVITY_OPTIONS,
    DIETARY_OPTIONS,
    EQUIPMENT_CHIPS,
    GENDER_OPTIONS,
    UNIT_OPTIONS,
    WEEKDAY_OPTIONS,
    WORKOUT_OPTIONS,
    buildOnboardingProfilePayload,
    convertLengthInputToMetric,
    convertWeightInputToMetric,
    ensureArray,
    getLengthDisplayValue,
    getWeightDisplayValue,
    getWorkoutTargetOptions,
    hydrateProfile,
    normalizePreferredWorkoutDays,
    normalizeWeeklyWorkoutTarget,
    toggleListValue,
    validateWorkoutDaySelection
} from '../utils/onboardingProfile';
import { getProfileEditorModeMeta } from '../utils/settingsPageEditorModes';
import { shouldNavigateToQuestGenerationDashboard } from '../utils/questGenerationUi';
import { getSubscriptionPlanLabel } from '../utils/subscriptionPageState';
import { buildRewardedUnlockCopy, REWARDED_UNLOCK_CONTEXTS } from '../utils/rewardedAds';
import { resolveDisplayName } from '../utils/displayName';
import { detectDeviceLocale, getLocaleLabel, SUPPORTED_LOCALES } from '../utils/locales';
import { useLocale } from '../context/LocaleContext';

const isProfileEmpty = (profile) => !profile || typeof profile !== 'object' || Object.keys(profile).length === 0;
const getNativeCurrentInstallPushStatus = () => {
    try {
        return window?.__FORZA_NATIVE_PUSH_STATUS__ || null;
    } catch (error) {
        return null;
    }
};
const unlockQuestRescheduleQuota = (quota) => {
    if (!quota || quota.featureKey !== AI_FEATURE_KEYS.QUESTS_PLANNER_RESCHEDULER) {
        return quota;
    }
    const rewardedAds = quota.rewardedAds
        ? {
            ...quota.rewardedAds,
            canStart: false,
            availableUses: Math.max(Number(quota.rewardedAds.availableUses || 0), Number(quota.rewardedAds.extraUsed || 0) + 1)
        }
        : quota.rewardedAds;

    return {
        ...quota,
        allowed: true,
        extraAvailable: Math.max(Number(quota.extraAvailable || 0), 1),
        rewardedAds,
        message: null
    };
};
const measurementUnitShort = (preferredUnits) => preferredUnits === 'imperial' ? 'in' : 'cm';
const weightUnitLabel = (preferredUnits) => preferredUnits === 'imperial' ? 'lbs' : 'kg';
const formatValue = (value, fallback = 'Not provided') => {
    if (Array.isArray(value)) {
        return value.length ? value.join(', ') : fallback;
    }
    if (value === undefined || value === null || value === '') {
        return fallback;
    }
    return String(value);
};
const weekdayLabels = (days) => {
    const selectedDays = normalizePreferredWorkoutDays(days);
    const labels = WEEKDAY_OPTIONS
        .filter((day) => selectedDays.includes(day.value))
        .map((day) => day.fullLabel || day.label);
    return labels.length ? labels.join(', ') : 'Not provided';
};

const ProfileDataDisplay = ({ profile, t }) => {
    if (isProfileEmpty(profile)) {
        return <p className="py-4 text-center text-gray-400">{t('settings.emptyProfile', 'No profile data available. Complete the onboarding flow to populate your profile.')}</p>;
    }

    const normalized = hydrateProfile(profile);
    const preferredUnits = normalized.equipmentAndPreferences.preferredUnits || 'metric';
    const lengthUnit = measurementUnitShort(preferredUnits);
    const weightUnit = weightUnitLabel(preferredUnits);
    const height = normalized.personalInformation.heightCm
        ? `${getLengthDisplayValue(normalized.personalInformation.heightCm, preferredUnits, 1, 0)} ${lengthUnit}`
        : '';
    const weight = normalized.personalInformation.weightKg
        ? `${getWeightDisplayValue(normalized.personalInformation.weightKg, preferredUnits)} ${weightUnit}`
        : '';
    const bodyMeasurementRows = [
        ['Neck', normalized.bodyComposition.neckCm],
        ['Chest', normalized.bodyComposition.chestCm],
        ['Waist', normalized.bodyComposition.waistCm],
        ['Hips', normalized.bodyComposition.hipsCm],
        ['Thigh', normalized.bodyComposition.thighCm],
    ];
    const selectedUnit = UNIT_OPTIONS.find((option) => option.value === preferredUnits)?.label || t('settings.metricUnits', 'Metric (kg, km)');

    const renderRow = (label, value) => (
        <div key={label} className="rounded-xl bg-slate-950/50 p-3.5 border border-white/5 shadow-inner">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
            <p className="mt-1 break-words text-sm font-medium text-slate-200">{formatValue(value)}</p>
        </div>
    );

    const renderSection = (title, rows) => (
        <div key={title} className="rounded-[1.5rem] bg-slate-900/40 p-5 shadow-xl border border-white/10 backdrop-blur-md">
            <h3 className="text-base font-bold text-white tracking-tight">{title}</h3>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">{rows.map(([label, value]) => renderRow(label, value))}</div>
        </div>
    );

    return (
        <div className="space-y-4">
            {renderSection(t('settings.profileSection', 'Profile'), [
                [t('settings.labels.fullName', 'Full name'), normalized.personalInformation.fullName],
                [t('settings.labels.age', 'Age'), normalized.personalInformation.age],
                [t('settings.labels.gender', 'Gender'), normalized.personalInformation.sex],
                [t('settings.labels.location', 'Location'), normalized.personalInformation.location],
                [t('settings.labels.height', 'Height'), height],
                [t('settings.labels.weight', 'Weight'), weight],
            ])}
            {renderSection(t('settings.trainingSection', 'Training'), [
                [t('settings.labels.exerciseFamiliarity', 'Exercise familiarity'), normalized.healthAndFitnessDetails.exerciseFamiliarity],
                [t('settings.labels.weeklyActivity', 'Weekly activity'), normalized.healthAndFitnessDetails.activityFrequency],
                [t('settings.labels.sessionsPerWeek', 'Sessions per week'), normalized.healthAndFitnessDetails.weeklyWorkoutTarget],
                [t('settings.labels.preferredWorkoutDays', 'Preferred workout days'), weekdayLabels(normalized.healthAndFitnessDetails.preferredWorkoutDays)],
                [t('settings.labels.fitnessGoals', 'Fitness goals'), normalized.healthAndFitnessDetails.fitnessGoals],
                [t('settings.labels.workoutStyles', 'Workout styles'), normalized.healthAndFitnessDetails.workoutPreferences],
            ])}
            {renderSection(t('settings.equipmentSection', 'Equipment'), [
                [t('settings.labels.preferredUnits', 'Preferred units'), selectedUnit],
                [t('settings.labels.availableEquipment', 'Available equipment'), normalized.equipmentAndPreferences.availableEquipment],
            ])}
            {renderSection(t('settings.bodyCompositionSection', 'Body composition'), [
                ...bodyMeasurementRows.map(([label, value]) => [
                    t(`settings.measurements.${label.toLowerCase()}`, label),
                    value ? `${getLengthDisplayValue(value, preferredUnits, 1, 0)} ${lengthUnit}` : '',
                ]),
                [t('settings.labels.notes', 'Notes'), normalized.bodyComposition.notes],
            ])}
            {renderSection(t('settings.healthNutritionSection', 'Health & nutrition'), [
                [t('settings.labels.medicalConditions', 'Medical conditions, injuries, or pain'), normalized.additionalInformation.medicalConditions],
                [t('settings.labels.dietaryPreferences', 'Dietary preferences'), normalized.additionalInformation.dietaryPreferences],
                [t('settings.labels.allergies', 'Allergies'), normalized.additionalInformation.allergies],
            ])}
        </div>
    );
};

const ProfileDataEditor = ({
    formState,
    onSectionChange,
    onCancel,
    onSave,
    isSaving,
    validation,
    mode = 'full'
}) => {
    const { t, locale } = useLocale();
    const [equipmentEntry, setEquipmentEntry] = useState('');
    const preferredUnits = formState.equipmentAndPreferences.preferredUnits || 'metric';
    const lengthUnit = measurementUnitShort(preferredUnits);
    const weightUnit = weightUnitLabel(preferredUnits);
    const targetOptions = getWorkoutTargetOptions(formState.healthAndFitnessDetails.activityFrequency);
    const weeklyWorkoutTarget = normalizeWeeklyWorkoutTarget(
        formState.healthAndFitnessDetails.weeklyWorkoutTarget,
        formState.healthAndFitnessDetails.activityFrequency
    );
    const preferredWorkoutDays = normalizePreferredWorkoutDays(formState.healthAndFitnessDetails.preferredWorkoutDays);
    const hasAllergies = formState.additionalInformation.allergies !== 'None';
    const isTrainingMode = mode === 'training';
    const saveButtonLabel = isTrainingMode
        ? t('profileEditor.saveTraining', 'Save training settings')
        : t('profileEditor.saveProfile', 'Save profile only');

    const updateSection = (section, payload) => onSectionChange(section, payload);
    const chipButton = ({ label, active, onClick, disabled = false, key = label }) => (
        <button
            key={key}
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${active
                ? 'border-cyan-400 bg-cyan-400/20 text-cyan-100'
                : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-500/50 disabled:cursor-not-allowed disabled:opacity-40'
                }`}
        >
            {label}
        </button>
    );

    const inputClass = 'rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3 text-sm text-white outline-none transition-all duration-200 focus:border-cyan-400 focus:bg-slate-900 focus:ring-1 focus:ring-cyan-400';
    const sectionShell = (title, children) => (
        <section key={title} className="rounded-[1.5rem] bg-slate-900/40 p-5 shadow-xl border border-white/10 backdrop-blur-md sm:p-6">
            <h3 className="text-base font-bold text-white tracking-tight">{title}</h3>
            <div className="mt-4">{children}</div>
        </section>
    );

    const selectActivityFrequency = (activityFrequency) => {
        const nextTarget = normalizeWeeklyWorkoutTarget(
            formState.healthAndFitnessDetails.weeklyWorkoutTarget,
            activityFrequency
        );
        updateSection('healthAndFitnessDetails', {
            activityFrequency,
            weeklyWorkoutTarget: nextTarget,
            preferredWorkoutDays: preferredWorkoutDays.slice(0, Number(nextTarget) || 0),
        });
    };

    const selectWeeklyWorkoutTarget = (target) => {
        updateSection('healthAndFitnessDetails', {
            weeklyWorkoutTarget: target,
            preferredWorkoutDays: preferredWorkoutDays.slice(0, target),
        });
    };

    const togglePreferredWorkoutDay = (day) => {
        const selected = preferredWorkoutDays.includes(day);
        const nextDays = selected
            ? preferredWorkoutDays.filter((item) => item !== day)
            : [...preferredWorkoutDays, day].slice(0, Number(weeklyWorkoutTarget) || 0);
        updateSection('healthAndFitnessDetails', { preferredWorkoutDays: nextDays });
    };

    const addEquipment = () => {
        const additions = equipmentEntry
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        if (!additions.length) {
            setEquipmentEntry('');
            return;
        }
        const merged = Array.from(new Set([...ensureArray(formState.equipmentAndPreferences.availableEquipment), ...additions]));
        updateSection('equipmentAndPreferences', { availableEquipment: merged });
        setEquipmentEntry('');
    };

    const suffix = locale.startsWith('it')
        ? (validation.expected === 1 ? 'o' : 'i')
        : locale.startsWith('es')
            ? (validation.expected === 1 ? '' : 's')
            : (validation.expected === 1 ? '' : 's');

    return (
        <form
            className="space-y-5"
            onSubmit={(e) => {
                e.preventDefault();
                onSave();
            }}
        >
            {!isTrainingMode && sectionShell(t('settings.profileSection', 'Profile'), (
                <div className="grid gap-5 md:grid-cols-2">
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.fullName', 'Full name')}</span>
                        <input type="text" value={formState.personalInformation.fullName} onChange={(event) => updateSection('personalInformation', { fullName: event.target.value })} className={inputClass} />
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.age', 'Age')}</span>
                        <input type="number" value={formState.personalInformation.age} onChange={(event) => updateSection('personalInformation', { age: event.target.value })} className={inputClass} />
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.height', 'Height')}</span>
                        <div className="relative">
                            <input type="number" value={getLengthDisplayValue(formState.personalInformation.heightCm, preferredUnits, 1, 0)} onChange={(event) => updateSection('personalInformation', { heightCm: convertLengthInputToMetric(event.target.value, preferredUnits) })} className={`${inputClass} w-full pr-12`} inputMode="decimal" step="any" />
                            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{lengthUnit}</span>
                        </div>
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.weight', 'Weight')}</span>
                        <div className="relative">
                            <input type="number" value={getWeightDisplayValue(formState.personalInformation.weightKg, preferredUnits)} onChange={(event) => updateSection('personalInformation', { weightKg: convertWeightInputToMetric(event.target.value, preferredUnits) })} className={`${inputClass} w-full pr-12`} inputMode="decimal" step="any" />
                            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{weightUnit}</span>
                        </div>
                    </label>
                    <div>
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.gender', 'Gender')}</span>
                        <div className="mt-3 grid grid-cols-3 gap-3">
                            {GENDER_OPTIONS.map((option) => chipButton({
                                label: t(`onboarding.options.gender.${option}`, option),
                                active: formState.personalInformation.sex === option,
                                onClick: () => updateSection('personalInformation', { sex: option }),
                            }))}
                        </div>
                    </div>
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.location', 'Location')}</span>
                        <input type="text" value={formState.personalInformation.location} onChange={(event) => updateSection('personalInformation', { location: event.target.value })} className={inputClass} />
                    </label>
                    <div className="md:col-span-2">
                        <span className="text-sm font-medium text-slate-200">{t('settings.labels.preferredUnits', 'Preferred units')}</span>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            {UNIT_OPTIONS.map((option) => chipButton({
                                label: t(`onboarding.options.units.${option.label}`, option.label),
                                active: preferredUnits === option.value,
                                onClick: () => updateSection('equipmentAndPreferences', { preferredUnits: option.value }),
                            }))}
                        </div>
                    </div>
                </div>
            ))}

            {sectionShell(t('settings.trainingSection', 'Training'), (
                <div className="space-y-7">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">{t('settings.labels.exerciseFamiliarity', 'Exercise familiarity')}</span>
                        <div className="mt-3 grid gap-3">
                            {EXERCISE_FAMILIARITY_OPTIONS.map((option) => {
                                const active = formState.healthAndFitnessDetails.exerciseFamiliarity === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => updateSection('healthAndFitnessDetails', { exerciseFamiliarity: option.value })}
                                        className={`rounded-2xl border p-4 text-left transition-colors ${active
                                            ? 'border-cyan-400 bg-cyan-400/15'
                                            : 'border-slate-700 bg-slate-900/70 hover:border-cyan-400/50'
                                            }`}
                                    >
                                        <p className="text-sm font-bold text-white">{t(`onboarding.options.familiarity.${option.value}`, option.label)}</p>
                                        <p className="mt-1 text-xs text-slate-400">{t(`onboarding.options.familiarityDescriptions.${option.value}`, option.description)}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <span className="text-sm font-semibold text-slate-200">{t('settings.labels.weeklyActivity', 'Weekly activity')}</span>
                        <div className="mt-3 grid gap-3 sm:grid-cols-4">
                            {ACTIVITY_OPTIONS.map((option) => chipButton({
                                label: t(`onboarding.options.activity.${option.value}`, option.label),
                                active: formState.healthAndFitnessDetails.activityFrequency === option.value,
                                onClick: () => selectActivityFrequency(option.value),
                            }))}
                        </div>
                    </div>
                    {formState.healthAndFitnessDetails.activityFrequency && (
                        <div className="space-y-5">
                            <div>
                                <span className="text-sm font-semibold text-slate-200">{t('settings.labels.sessionsPerWeek', 'Sessions per week')}</span>
                                <div className="mt-3 grid gap-3 sm:grid-cols-4">
                                    {targetOptions.map((target) => chipButton({
                                        label: target.toString(),
                                        active: weeklyWorkoutTarget === target,
                                        onClick: () => selectWeeklyWorkoutTarget(target),
                                    }))}
                                </div>
                            </div>
                            <div>
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                                    <span className="text-sm font-semibold text-slate-200">{t('settings.labels.preferredWorkoutDays', 'Preferred workout days')}</span>
                                    <span className={`text-xs font-medium ${validation.valid ? 'text-slate-500' : 'text-amber-200'}`}>
                                        {t('onboarding.preferredWorkoutDaysCount', '{selected}/{total} selected', {
                                            selected: preferredWorkoutDays.length,
                                            total: weeklyWorkoutTarget || 0,
                                        })}
                                    </span>
                                </div>
                                <div className="mt-3 grid grid-cols-4 gap-3 sm:grid-cols-7">
                                    {WEEKDAY_OPTIONS.map((day) => {
                                        const active = preferredWorkoutDays.includes(day.value);
                                        const disabled = !active && preferredWorkoutDays.length >= Number(weeklyWorkoutTarget || 0);
                                        return chipButton({
                                            key: day.value,
                                            label: t(`onboarding.options.weekdays.${day.label}`, day.label),
                                            active,
                                            disabled,
                                            onClick: () => togglePreferredWorkoutDay(day.value),
                                        });
                                    })}
                                </div>
                                {!validation.valid && (
                                    <p className="mt-2 text-xs font-medium text-amber-200">
                                        {t('profileEditor.validationWorkoutDays', 'Choose exactly {expected} workout day{suffix} before saving.', { expected: validation.expected, suffix })}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    {!isTrainingMode && (
                        <>
                            <label className="block">
                                <span className="text-sm font-semibold text-slate-200">{t('settings.labels.fitnessGoals', 'Fitness goals')}</span>
                                <textarea rows={4} value={formState.healthAndFitnessDetails.fitnessGoals} onChange={(event) => updateSection('healthAndFitnessDetails', { fitnessGoals: event.target.value })} className={`${inputClass} mt-3 min-h-[120px] w-full`} />
                            </label>
                            <div>
                                <p className="text-sm font-semibold text-slate-200">{t('settings.labels.workoutStyles', 'Favourite workout styles')}</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {WORKOUT_OPTIONS.map((option) => chipButton({
                                        label: t(`onboarding.options.workout.${option}`, option),
                                        active: formState.healthAndFitnessDetails.workoutPreferences.includes(option),
                                        onClick: () => updateSection('healthAndFitnessDetails', {
                                            workoutPreferences: toggleListValue(formState.healthAndFitnessDetails.workoutPreferences, option),
                                        }),
                                    }))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ))}

            {!isTrainingMode && sectionShell(t('settings.equipmentSection', 'Equipment'), (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {EQUIPMENT_CHIPS.map((option) => chipButton({
                            label: t(`onboarding.options.equipment.${option}`, option),
                            active: formState.equipmentAndPreferences.availableEquipment.includes(option),
                            onClick: () => updateSection('equipmentAndPreferences', {
                                availableEquipment: toggleListValue(formState.equipmentAndPreferences.availableEquipment, option),
                            }),
                        }))}
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <input
                            type="text"
                            value={equipmentEntry}
                            onChange={(event) => setEquipmentEntry(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    addEquipment();
                                }
                            }}
                            placeholder={t('profileEditor.addCustomEquipment', 'Add custom equipment')}
                            className={`${inputClass} flex-1`}
                        />
                        <button type="button" onClick={addEquipment} className="rounded-xl bg-cyan-500 px-5 py-3 text-sm font-bold text-slate-950">{t('profileEditor.add', 'Add')}</button>
                    </div>
                </div>
            ))}

            {!isTrainingMode && sectionShell(t('settings.bodyCompositionSection', 'Body composition'), (
                <div className="space-y-5">
                    <div className="grid gap-5 md:grid-cols-2">
                        {[
                            ['neckCm', 'Neck'],
                            ['chestCm', 'Chest'],
                            ['waistCm', 'Waist'],
                            ['hipsCm', 'Hips'],
                            ['thighCm', 'Thigh'],
                        ].map(([key, label]) => (
                            <label key={key} className="flex flex-col gap-2">
                                <span className="text-sm font-medium text-slate-200">{t(`settings.measurements.${key.replace('Cm', '')}`, label)}</span>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={getLengthDisplayValue(formState.bodyComposition[key], preferredUnits)}
                                        onChange={(event) => updateSection('bodyComposition', { [key]: convertLengthInputToMetric(event.target.value, preferredUnits) })}
                                        className={`${inputClass} w-full pr-12`}
                                        inputMode="decimal"
                                        step="any"
                                    />
                                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{lengthUnit}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-slate-200">{t('profileEditor.optionalNotes', 'Optional notes')}</span>
                        <textarea rows={3} value={formState.bodyComposition.notes} onChange={(event) => updateSection('bodyComposition', { notes: event.target.value })} className={`${inputClass} min-h-[100px]`} />
                    </label>
                </div>
            ))}

            {!isTrainingMode && sectionShell(t('settings.healthNutritionSection', 'Health & nutrition'), (
                <div className="space-y-6">
                    <label className="block">
                        <span className="text-sm font-semibold text-slate-200">{t('settings.labels.medicalConditions', 'Medical conditions, injuries, or pain')}</span>
                        <textarea rows={4} value={formState.additionalInformation.medicalConditions} onChange={(event) => updateSection('additionalInformation', { medicalConditions: event.target.value })} className={`${inputClass} mt-3 min-h-[120px] w-full`} />
                    </label>
                    <div>
                        <p className="text-sm font-semibold text-slate-200">{t('settings.labels.dietaryPreferences', 'Dietary preferences')}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {DIETARY_OPTIONS.map((option) => chipButton({
                                label: t(`onboarding.options.dietary.${option}`, option),
                                active: formState.additionalInformation.dietaryPreferences.includes(option),
                                onClick: () => updateSection('additionalInformation', {
                                    dietaryPreferences: toggleListValue(formState.additionalInformation.dietaryPreferences, option),
                                }),
                            }))}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3">
                            <span className="text-sm font-medium text-slate-200">{t('profileEditor.allergiesCheckbox', 'I have allergies')}</span>
                            <input
                                type="checkbox"
                                checked={hasAllergies}
                                onChange={() => {
                                    const next = !hasAllergies;
                                    updateSection('additionalInformation', { allergies: next ? '' : 'None' });
                                }}
                                className="size-5 accent-cyan-500"
                            />
                        </label>
                        {hasAllergies && (
                            <textarea rows={3} value={formState.additionalInformation.allergies === 'None' ? '' : formState.additionalInformation.allergies} onChange={(event) => updateSection('additionalInformation', { allergies: event.target.value })} placeholder={t('profileEditor.allergiesPlaceholder', 'List allergies or reactions.')} className={`${inputClass} min-h-[100px] w-full`} />
                        )}
                    </div>
                </div>
            ))}

            <div className="border-t border-white/10 pt-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-slate-800/50 px-6 py-2.5 text-sm font-bold tracking-wide text-white transition-all duration-200 hover:bg-slate-700/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 active:scale-[0.98]"
                    >
                        {t('profileEditor.cancel', 'Cancel')}
                    </button>
                    <button
                        type="submit"
                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2.5 text-sm font-bold tracking-wide text-white shadow-lg transition-all duration-200 hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isSaving || !validation.valid}
                    >
                        {isSaving && (
                            <svg
                                className="mr-2 size-4 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                />
                            </svg>
                        )}
                        {isSaving ? t('profileEditor.saving', 'Saving...') : saveButtonLabel}
                    </button>
                </div>
            </div>
        </form>
    );
};

const FitnessAssessmentSettingsCard = ({
    fitnessAssessment,
    currentLevel,
    onSubmit,
    isSubmitting,
    disabled,
    locale,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [answers, setAnswers] = useState(createInitialAssessmentAnswers);
    const score = scoreFitnessAssessment(answers);
    const submittedAt = fitnessAssessment?.submittedAt;
    const status = submittedAt ? 'submitted' : fitnessAssessment?.status;
    const isLocked = Boolean(submittedAt);
    const localizedDomains = FITNESS_ASSESSMENT_DOMAINS.map((domain) => ({
        ...domain,
        label: t(`onboarding.assessmentDomains.${domain.id}.label`, domain.label),
        prompt: t(`onboarding.assessmentDomains.${domain.id}.prompt`, domain.prompt),
        unit: t(`onboarding.assessmentUnits.${domain.unit}`, domain.unit),
        variations: domain.variations.map((variation) => ({
            ...variation,
            difficultyLabel: t(`onboarding.assessmentDifficulty.${variation.value}`, variation.difficultyLabel || variation.value),
            label: t(`onboarding.assessmentDomains.${domain.id}.variations.${variation.value}`, variation.label),
            description: t(`onboarding.assessmentDomains.${domain.id}.variationDescriptions.${variation.value}`, ''),
        })),
    }));

    const handleSubmit = () => {
        const willRaiseLevel = score.assignedLevel > currentLevel;
        if (willRaiseLevel) {
            const confirmed = window.confirm(t('settings.assessment.raiseLevelConfirm', 'Submitting this one-time assessment will raise your starting baseline and regenerate future incomplete quests. Continue?'));
            if (!confirmed) {
                return;
            }
        }
        onSubmit(score.answers);
    };

    return (
        <section className="relative overflow-hidden rounded-[2rem] bg-slate-900/60 p-5 shadow-2xl border border-white/10 backdrop-blur-2xl sm:p-6">
            <div className="relative z-10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h2 className="text-lg font-semibold text-white">{t('settings.assessment.title', 'Fitness Assessment')}</h2>
                            <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.25em] ${isLocked
                                ? 'border border-emerald-500/40 bg-emerald-900/30 text-emerald-200'
                                : 'border border-cyan-500/40 bg-cyan-900/30 text-cyan-200'
                                }`}>
                                {isLocked ? t('settings.assessment.completed', 'Completed') : status === 'skipped' ? t('settings.assessment.skipped', 'Skipped') : t('settings.assessment.available', 'Available')}
                            </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-400">
                            {t('settings.assessment.description', 'Complete this one-time self-report baseline to recalibrate future quest difficulty. If your current level is higher, your earned level is preserved.')}
                        </p>
                    </div>
                    {!isLocked && (
                        <button
                            type="button"
                            onClick={() => setIsOpen((prev) => !prev)}
                            disabled={disabled || isSubmitting}
                            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2.5 text-sm font-bold tracking-wide text-white shadow-lg transition-all duration-200 hover:from-cyan-400 hover:to-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isOpen ? t('settings.assessment.close', 'Close assessment') : t('settings.assessment.start', 'Start assessment')}
                        </button>
                    )}
                </div>

                {isLocked && (
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                        <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{t('settings.assessment.assignedLevel', 'Assigned level')}</p>
                            <p className="mt-1 text-2xl font-black text-white">{fitnessAssessment.assignedLevel}</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{t('settings.assessment.submitted', 'Submitted')}</p>
                            <p className="mt-1 text-sm font-bold text-white">{new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(submittedAt))}</p>
                        </div>
                    </div>
                )}

                {isOpen && !isLocked && (
                    <div className="mt-6 space-y-4">
                        <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4">
                            <p className="text-sm font-bold text-cyan-100">{t('settings.assessment.qualityHeadline', 'Choose controlled movement quality over maximum numbers.')}</p>
                            <p className="mt-1 text-xs text-cyan-100/80">
                                {t('settings.assessment.qualityBody', 'Pick the hardest variation you can perform safely and enter the result. Submission is one-time.')}
                            </p>
                        </div>
                        {localizedDomains.map((domain) => {
                            const selectedVariation = answers[domain.id]?.variation;
                            const hasResultInput = domain.resultInputType !== 'none';
                            return (
                                <div key={domain.id} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <h3 className="text-sm font-bold text-white">{domain.label}</h3>
                                            <p className="mt-1 text-xs text-slate-500">{domain.prompt}</p>
                                        </div>
                                    </div>
                                    <div className={hasResultInput ? 'mt-4 grid gap-3 lg:grid-cols-[1fr_170px]' : 'mt-4'}>
                                        <div className={`grid gap-2 ${hasResultInput ? 'sm:grid-cols-3' : 'sm:grid-cols-5'}`}>
                                            {domain.variations.map((variation) => {
                                                const active = selectedVariation === variation.value;
                                                const style = FITNESS_ASSESSMENT_DIFFICULTY_STYLES[variation.value] || FITNESS_ASSESSMENT_DIFFICULTY_STYLES.standard;
                                                return (
                                                    <button
                                                        key={variation.value}
                                                        type="button"
                                                        onClick={() => setAnswers((prev) => ({
                                                            ...prev,
                                                            [domain.id]: { ...prev[domain.id], variation: variation.value }
                                                        }))}
                                                        className={`rounded-2xl border p-3 text-left transition-all ${active
                                                            ? style.className
                                                            : 'border-white/10 bg-slate-950/50 text-slate-300 hover:border-white/30'
                                                            }`}
                                                    >
                                                        <span className="text-[10px] font-black uppercase tracking-[0.22em]">{variation.difficultyLabel || style.label}</span>
                                                        <span className="mt-1 block text-xs font-bold sm:text-sm">{variation.label}</span>
                                                        {variation.description ? (
                                                            <span className="mt-2 block text-[11px] font-medium leading-5 text-slate-300">
                                                                {variation.description}
                                                            </span>
                                                        ) : null}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {hasResultInput && (
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={answers[domain.id]?.result}
                                                    onChange={(event) => setAnswers((prev) => ({
                                                        ...prev,
                                                        [domain.id]: { ...prev[domain.id], result: event.target.value }
                                                    }))}
                                                    placeholder={t('settings.assessment.resultPlaceholder', 'Result')}
                                                    className="w-full rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3 pr-20 text-base text-white outline-none transition-all duration-200 focus:border-cyan-400 focus:bg-slate-900 focus:ring-1 focus:ring-cyan-400 sm:text-sm"
                                                />
                                                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{domain.unit}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="rounded-2xl border border-amber-500/30 bg-amber-900/20 p-4 text-xs font-medium text-amber-100">
                            {t('settings.assessment.safetyNote', 'This assessment is self-reported and is not medical clearance. Stop if anything feels wrong.')}
                        </div>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting || disabled}
                            className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 text-sm font-bold tracking-wide text-white shadow-lg transition-all duration-200 hover:from-cyan-400 hover:to-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isSubmitting ? t('settings.assessment.submitting', 'Submitting assessment...') : t('settings.assessment.submit', 'Submit one-time assessment')}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

const STATUS_TONE_CLASSES = {
    neutral: 'border-[#334155] bg-[#172033] text-[#e2e8f0]',
    info: 'border-[#17689d] bg-[#0c2238] text-[#7dd3fc]',
    success: 'border-[#1f6d54] bg-[#0c2e28] text-[#6ee7b7]',
    warning: 'border-[#7c5b17] bg-[#342810] text-[#fbbf24]',
    danger: 'border-[#7f1d1d] bg-[#321316] text-[#fca5a5]'
};

const StatusPill = ({ children, tone = 'neutral' }) => (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_TONE_CLASSES[tone] || STATUS_TONE_CLASSES.neutral}`}>
        {children}
    </span>
);

const SurfaceCard = React.forwardRef(({ children, className = '' }, ref) => (
    <section
        ref={ref}
        className={`overflow-hidden rounded-[1.5rem] border border-[#223651] bg-[#101a2b]/95 shadow-2xl backdrop-blur-xl ${className}`}
    >
        {children}
    </section>
));

SurfaceCard.displayName = 'SurfaceCard';

const SectionLabel = ({ children }) => (
    <p className="px-4 text-[0.72rem] font-black uppercase tracking-[0.22em] text-[#8798b5]">
        {children}
    </p>
);

const IconBadge = ({ icon: Icon, destructive = false, success = false }) => {
    const shellClassName = destructive
        ? 'border-red-500/30 bg-red-500/10 text-red-300'
        : success
            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
            : 'border-[#21364d] bg-[#111d2f] text-cyan-300';

    return (
        <div className={`flex size-12 shrink-0 items-center justify-center rounded-2xl border ${shellClassName}`}>
            <Icon size={22} strokeWidth={1.9} />
        </div>
    );
};

const QuickActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    disabled = false,
    locked = false,
    loading = false,
    tone = 'blue'
}) => {
    const toneClassName = tone === 'cyan'
        ? 'border-cyan-500/20 bg-[#0f1e2e]'
        : 'border-[#223651] bg-[#101a2b]';

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            className={`w-full border px-4 py-4 text-left transition-all duration-200 hover:border-cyan-400/50 disabled:cursor-not-allowed disabled:opacity-50 ${locked ? 'cursor-not-allowed border-amber-500/35 bg-slate-900/70 opacity-45 grayscale hover:border-amber-500/35' : toneClassName}`}
        >
            <div className="flex items-center gap-4">
                <IconBadge icon={Icon} />
                <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold tracking-tight text-white">{title}</h3>
                    <p className="mt-1 text-sm leading-5 text-[#9aaac2]">{description}</p>
                </div>
                {loading ? (
                    <div className="size-5 animate-spin rounded-full border-2 border-cyan-300/20 border-t-cyan-300" />
                ) : (
                    <ChevronRight size={22} className="text-slate-300" strokeWidth={1.8} />
                )}
            </div>
        </button>
    );
};

const SnapshotTile = ({ icon: Icon, label, value, index }) => (
    <div className={`min-w-0 border-[#24344b] px-4 py-4 ${index % 2 === 0 ? 'border-r' : ''} ${index > 1 ? 'border-t' : ''}`}>
        <div className="flex items-center gap-3">
            <Icon size={19} className="shrink-0 text-cyan-300" strokeWidth={1.9} />
            <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">{label}</p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-100">{value}</p>
            </div>
        </div>
    </div>
);

const SettingRow = ({
    icon: Icon,
    title,
    description,
    onClick,
    destructive = false,
    disabled = false,
    children = null
}) => {
    const shellClassName = destructive
        ? 'border-red-500/45 bg-red-950/25'
        : 'border-white/10 bg-[#0b1322]';
    const titleClassName = destructive ? 'text-red-300' : 'text-white';

    const content = (
        <div className={`flex w-full items-center gap-4 px-4 py-4 transition-colors duration-200 ${shellClassName} ${disabled ? 'opacity-50' : 'hover:border-cyan-400/30'}`}>
            <IconBadge icon={Icon} destructive={destructive} />
            <div className="min-w-0 flex-1">
                <p className={`text-base font-semibold ${titleClassName}`}>{title}</p>
                <p className={`mt-1 text-sm leading-5 ${destructive ? 'text-red-200/80' : 'text-[#9aaac2]'}`}>{description}</p>
                {children}
            </div>
            <ChevronRight size={22} className={destructive ? 'text-red-300' : 'text-slate-400'} strokeWidth={1.8} />
        </div>
    );

    if (!onClick) {
        return content;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="w-full text-left"
        >
            {content}
        </button>
    );
};

const SettingsPage = () => {
    const { user, logout, updateUser } = useAuth();
    const { t, locale } = useLocale();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        profileDataJson: '{}'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');
    const [profileData, setProfileData] = useState({});
    const [draftProfileData, setDraftProfileData] = useState(() => hydrateProfile({}));
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const pageTopRef = useRef(null);
    const profileEditorRef = useRef(null);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const navigate = useNavigate();
    const [hasProfileData, setHasProfileData] = useState(true);
    const [avatarFailed, setAvatarFailed] = useState(false);
    const [generationStatus, setGenerationStatus] = useState({ state: 'idle', job: null });
    const [aiFeatureQuotas, setAiFeatureQuotas] = useState(null);
    const [fitnessAssessment, setFitnessAssessment] = useState(null);
    const [isSubmittingAssessment, setIsSubmittingAssessment] = useState(false);
    const statusPollRef = useRef(null);
    const isMountedRef = useRef(true);
    const [isNativeApp, setIsNativeApp] = useState(false);
    const [profileEditorMode, setProfileEditorMode] = useState(null);
    const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
    const [pushNotificationStatus, setPushNotificationStatus] = useState(null);
    const [isPushNotificationsPending, setIsPushNotificationsPending] = useState(false);
    const [isPushTestPending, setIsPushTestPending] = useState(false);
    const [pushTestDiagnostic, setPushTestDiagnostic] = useState(null);
    const [showPushSettingsButton, setShowPushSettingsButton] = useState(false);
    const [preferredLocale, setPreferredLocale] = useState(() => detectDeviceLocale());
    const [isSavingLocale, setIsSavingLocale] = useState(false);

    const getPushNotificationsDescription = useCallback(() => {
        if (pushNotificationStatus?.status === 'needs_setup') {
            return t('settings.push.needsSetup', 'Notifications were requested, but this iPhone still needs to sync a valid push token.');
        }
        if (pushNotificationStatus?.status === 'permission_denied') {
            return t('settings.push.permissionDenied', 'Notifications are blocked in iPhone Settings.');
        }
        return pushNotificationsEnabled
            ? t('settings.push.enabledDescription', 'Quest, streak, leaderboard, and duel notifications are enabled on this iPhone.')
            : t('settings.push.disabledDescription', 'Quest, streak, leaderboard, and duel notifications are disabled on this iPhone.');
    }, [pushNotificationStatus, pushNotificationsEnabled, t]);

    const formatTriggerSource = useCallback((source) => {
        if (!source || typeof source !== 'string') {
            return 'system';
        }
        return source
            .split(/[-_]/)
            .filter(Boolean)
            .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleOpenHealthKitSettings = () => {
        try {
            if (window && window.forzaNative && typeof window.forzaNative.openHealthKitSettings === 'function') {
                window.forzaNative.openHealthKitSettings();
            }
        } catch (err) {
            console.warn('Failed to open HealthKit settings.');
        }
    };

    const handlePushNotificationsPress = useCallback(() => {
        setWarningMessage('');
        setFeedbackMessage('');

        if (!isNativeApp || !window?.forzaNative?.setPushNotificationsEnabled) {
            setWarningMessage(t('settings.push.iosOnly', 'Push notifications are currently available in the iOS app only.'));
            return;
        }

        setIsPushNotificationsPending(true);
        window.forzaNative.setPushNotificationsEnabled(!pushNotificationsEnabled);
    }, [isNativeApp, pushNotificationsEnabled, t]);

    const handleOpenNativeSettings = useCallback(() => {
        if (!window?.forzaNative?.openAppSettings) {
            return;
        }
        window.forzaNative.openAppSettings();
    }, []);

    const handleSendTestPush = useCallback(() => {
        setFeedbackMessage('');
        setWarningMessage('');
        if (!isNativeApp || !window?.forzaNative?.sendTestPushNotification) {
            setWarningMessage(t('settings.push.iosOnly', 'Push notifications are currently available in the iOS app only.'));
            return;
        }

        setIsPushTestPending(true);
        window.forzaNative.sendTestPushNotification();
    }, [isNativeApp, t]);

    const refreshGenerationStatus = useCallback(async () => {
        try {
            const response = await getQuestGenerationStatus();
            if (isMountedRef.current && response?.data) {
                setGenerationStatus(response.data);
            }
        } catch (error) {
            console.error('Could not fetch quest generation status.', error);
        }
    }, []);

    const refreshAiFeatureQuotas = useCallback(async () => {
        try {
            const response = await getAiFeatureQuotas();
            if (isMountedRef.current) {
                setAiFeatureQuotas(response?.data || null);
            }
        } catch (error) {
            if (isMountedRef.current) {
                setAiFeatureQuotas(null);
            }
            console.error('Could not fetch AI feature quotas.', error);
        }
    }, []);

    const [showFeedback, setShowFeedback] = useState(false);

    useEffect(() => {
        try {
            setIsNativeApp(Boolean(window && window.__FORZA_NATIVE_APP__));
        } catch (err) {
            setIsNativeApp(false);
        }
    }, []);

    const handleResetGuide = () => {
        localStorage.removeItem('forza_banner_dismissed');
        setShowFeedback(true);
        // Hide the message automatically after 3 seconds
        setTimeout(() => setShowFeedback(false), 3000);
    };

    const populateForm = (profile) => {
        const normalizeProfile = (payload) => {
            if (!payload || typeof payload !== 'object') return {};
            if (payload.profile && typeof payload.profile === 'object') {
                if (payload.profile.profile && typeof payload.profile.profile === 'object') {
                    return payload.profile.profile;
                }
                return payload.profile;
            }
            return payload;
        };

        const profilePayload = profile.profileData ?? profile.medicalData ?? {};
        const profileToDisplay = normalizeProfile(profilePayload);

        const profileOnlyJson = Object.keys(profileToDisplay).length > 0 ? JSON.stringify(profileToDisplay, null, 2) : '{}';

        setFormData({
            username: profile.username || '',
            email: profile.email || '',
            profileDataJson: profileOnlyJson
        });
        setPreferredLocale(profile.preferredLocale || detectDeviceLocale());
        console.info('[Locale][WebSettingsHydrate]', {
            preferredLocale: profile.preferredLocale || detectDeviceLocale(),
            username: profile.username || null,
        });
        setProfileData(profileToDisplay);
        setDraftProfileData(hydrateProfile(profileToDisplay));
        setFitnessAssessment(profile.fitnessAssessment || null);
        setProfileEditorMode(null);
        setHasProfileData(Object.keys(profileToDisplay).length > 0);
        const nativePushStatus = getNativeCurrentInstallPushStatus();
        const effectivePushStatus = nativePushStatus || profile.pushNotificationStatus || null;
        setPushNotificationStatus(effectivePushStatus);
        setPushNotificationsEnabled(Boolean(effectivePushStatus?.enabled ?? profile.enablePushNotifications));
        setShowPushSettingsButton(false);
        setPushTestDiagnostic(null);
    };

    useEffect(() => {
        if (user) {
            getUserProfile().then(res => {
                populateForm(res.data);
            }).catch(err => console.error("Could not fetch user profile", err));
            refreshGenerationStatus();
            refreshAiFeatureQuotas();
        }
    }, [user, refreshGenerationStatus, refreshAiFeatureQuotas]);

    useEffect(() => {
        const handleNativeEvent = (event) => {
            const payload = event?.detail;
            if (!payload || payload.type !== 'FORZA_PUSH_NOTIFICATIONS_RESULT') {
                return;
            }

            if (payload.source === 'test_push') {
                setIsPushTestPending(false);
                if (payload.user) {
                    updateUser(payload.user);
                }
                const nextPushStatus = payload.currentInstallPushNotificationStatus || payload.user?.pushNotificationStatus || null;
                if (nextPushStatus) {
                    setPushNotificationStatus(nextPushStatus);
                    setPushNotificationsEnabled(Boolean(nextPushStatus.enabled));
                }

                const diagnostic = payload.diagnostic ? {
                    ...payload.diagnostic,
                    message: payload.diagnostic.message || payload.error || null,
                } : {
                    status: payload.status || 'error',
                    message: payload.error || t('settings.push.testFailed', 'Failed to send test push notification.'),
                };
                setPushTestDiagnostic(diagnostic);

                if (diagnostic.status === 'delivered') {
                    setWarningMessage('');
                    setFeedbackMessage(t('settings.push.testDelivered', 'Test notification delivered successfully.'));
                } else if (diagnostic.status === 'pending' || diagnostic.status === 'accepted') {
                    setWarningMessage('');
                    setFeedbackMessage(t('settings.push.testPending', 'Test notification accepted. Receipt confirmation is still pending.'));
                } else {
                    setFeedbackMessage('');
                    setWarningMessage(diagnostic.message || t('settings.push.testFailed', 'Failed to send test push notification.'));
                }
                return;
            }

            setIsPushNotificationsPending(false);

            if (payload.user) {
                updateUser(payload.user);
            }
            const nextPushStatus = payload.currentInstallPushNotificationStatus || payload.user?.pushNotificationStatus || null;
            if (nextPushStatus) {
                setPushNotificationStatus(nextPushStatus);
            }

            if (payload.status === 'enabled') {
                setPushNotificationsEnabled(true);
                setPushNotificationStatus(nextPushStatus || {
                    enabled: true,
                    status: 'enabled',
                });
                setShowPushSettingsButton(false);
                setWarningMessage('');
                setFeedbackMessage(t('settings.push.enabledFeedback', 'Push notifications enabled.'));
                return;
            }

            if (payload.status === 'synced') {
                setPushNotificationsEnabled(true);
                setPushNotificationStatus(nextPushStatus || {
                    enabled: true,
                    status: 'enabled',
                });
                setShowPushSettingsButton(false);
                return;
            }

            if (payload.status === 'disabled') {
                setPushNotificationsEnabled(false);
                setPushNotificationStatus(nextPushStatus || {
                    enabled: false,
                    status: 'disabled',
                });
                setShowPushSettingsButton(false);
                setPushTestDiagnostic(null);
                setWarningMessage('');
                setFeedbackMessage(t('settings.push.disabledFeedback', 'Push notifications disabled.'));
                return;
            }

            if (payload.status === 'disabled_local') {
                setPushNotificationsEnabled(false);
                setPushNotificationStatus({
                    enabled: false,
                    status: 'disabled',
                });
                setShowPushSettingsButton(false);
                return;
            }

            if (payload.status === 'disabled_external') {
                setPushNotificationsEnabled(false);
                setPushNotificationStatus(nextPushStatus || {
                    enabled: false,
                    status: 'permission_denied',
                });
                setShowPushSettingsButton(true);
                setPushTestDiagnostic(null);
                setFeedbackMessage('');
                setWarningMessage(t('settings.push.disabledInSettings', 'Push notifications were disabled in iPhone Settings.'));
                return;
            }

            if (payload.status === 'denied') {
                setPushNotificationsEnabled(false);
                setPushNotificationStatus(nextPushStatus || {
                    enabled: false,
                    status: 'permission_denied',
                });
                setShowPushSettingsButton(true);
                setPushTestDiagnostic(null);
                setFeedbackMessage('');
                setWarningMessage(t('settings.push.deniedHelp', 'Notifications are disabled in iPhone Settings. Enable them there, then try again.'));
                return;
            }

            if (payload.status === 'error') {
                setFeedbackMessage('');
                setWarningMessage(payload.error || t('settings.push.updateFailed', 'Failed to update push notifications.'));
            }
        };

        window.addEventListener('forza:native', handleNativeEvent);
        return () => window.removeEventListener('forza:native', handleNativeEvent);
    }, [t, updateUser]);

    useEffect(() => {
        setAvatarFailed(false);
    }, [user?.avatarUrl]);

    const pushTestReceiptError = pushTestDiagnostic?.receipt?.details?.error
        || pushTestDiagnostic?.receipt?.raw?.details?.error
        || null;
    const pushTestStatusLabel = pushTestDiagnostic?.status === 'delivered'
        ? t('settings.push.testStatusDelivered', 'Delivered')
        : pushTestDiagnostic?.status === 'pending'
            ? t('settings.push.testStatusPending', 'Pending')
            : pushTestDiagnostic?.status === 'accepted'
                ? t('settings.push.testStatusAccepted', 'Accepted')
                : pushTestDiagnostic?.status === 'not_ready'
                    ? t('settings.push.testStatusNotReady', 'Not ready')
                    : t('settings.push.testStatusError', 'Error');
    const pushTestStatusToneClassName = pushTestDiagnostic?.status === 'delivered'
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
        : pushTestDiagnostic?.status === 'pending' || pushTestDiagnostic?.status === 'accepted'
            ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-100'
            : 'border-amber-500/30 bg-amber-500/10 text-amber-100';

    useEffect(() => {
        if (!profileEditorMode || !profileEditorRef.current) {
            return;
        }

        profileEditorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }, [profileEditorMode]);

    const handleQuestReschedule = async (e) => {
        e.preventDefault();
        if (plannerQuotaBlocked) {
            if (canOfferPlannerRewardedUnlock) {
                handleQuestRescheduleRewardedUnlock();
                return;
            }
            setWarningMessage(plannerQuotaMessage || t('settings.plan.limitReached', 'Quest regeneration limit reached.'));
            return;
        }
        setIsLoading(true);
        setError('');
        setFeedbackMessage('');
        setWarningMessage('');
        try {
            const profilePayload = {
                username: formData.username,
                email: formData.email,
                profileData: profileData,
                preferredLocale
            };

            const response = await saveUserProfile(profilePayload);
            setFeedbackMessage(response.data.message);
            await refreshGenerationStatus();
            await refreshAiFeatureQuotas();
            try {
                const latestProfile = await getUserProfile();
                populateForm(latestProfile.data);
                updateUser(latestProfile.data);
            } catch (refreshError) {
                console.error("Could not refresh user profile after save", refreshError);
            }

            const schedulingStatus = response.data?.scheduling?.status;
            if (shouldNavigateToQuestGenerationDashboard(schedulingStatus)) {
                setTimeout(() => {
                    navigate('/dashboard', {
                        state: {
                            isGenerating: true,
                            generationRequestStatus: schedulingStatus
                        }
                    });
                }, 2500); // 1.5 second delay
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data?.msg || t('settings.errors.save', 'An error occurred while saving.');
            if (err.response?.status === 429) {
                const blockedQuota = err.response?.data?.scheduling?.quota || err.response?.data?.quota || null;
                if (blockedQuota?.featureKey === AI_FEATURE_KEYS.QUESTS_PLANNER_RESCHEDULER) {
                    setAiFeatureQuotas(prev => ({
                        ...(prev || {}),
                        tier: prev?.tier || blockedQuota.tier || user?.subscription?.status || 'free-tier',
                        features: {
                            ...(prev?.features || {}),
                            [AI_FEATURE_KEYS.QUESTS_PLANNER_RESCHEDULER]: blockedQuota
                        }
                    }));
                } else {
                    setWarningMessage(errorMessage);
                }
            } else {
                setError(errorMessage);
            }
            await refreshAiFeatureQuotas();
        } finally {
            setIsLoading(false);
        }
    };

    const handleProfileSectionChange = useCallback((section, payload) => {
        setDraftProfileData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                ...payload
            }
        }));
    }, []);

    const enterProfileEditMode = () => {
        setDraftProfileData(hydrateProfile(profileData));
        setProfileEditorMode('full');
        setError('');
        setWarningMessage('');
    };

    const enterTrainingEditMode = () => {
        setDraftProfileData(hydrateProfile(profileData));
        setProfileEditorMode('training');
        setError('');
    };

    const handleProfileEditCancel = () => {
        setDraftProfileData(hydrateProfile(profileData));
        setProfileEditorMode(null);
    };

    const handleProfileEditSave = async () => {
        const workoutDayValidation = validateWorkoutDaySelection(draftProfileData);
        if (!workoutDayValidation.valid) {
            setError(`Choose exactly ${workoutDayValidation.expected} workout day${workoutDayValidation.expected === 1 ? '' : 's'} before saving.`);
            return;
        }
        setIsSavingProfile(true);
        setError('');
        setFeedbackMessage('');
        try {
            const hasAllergies = draftProfileData.additionalInformation.allergies !== 'None';
            const payload = {
                username: formData.username,
                email: formData.email,
                profileData: buildOnboardingProfilePayload(draftProfileData, { hasAllergies }),
                preferredLocale,
                suppressQuestRegeneration: true
            };
            const response = await saveUserProfile(payload);

            try {
                const latestProfile = await getUserProfile();
                populateForm(latestProfile.data);
                updateUser(latestProfile.data);
            } catch (refreshError) {
                console.error("Could not refresh user profile after edit save", refreshError);
            }
            setFeedbackMessage(response.data?.message || t('settings.profileUpdatedNoQuestChange', 'Profile updated. Quests remain unchanged.'));
        } catch (err) {
            const errorMessage = err.response?.data?.msg || err.response?.data?.message || t('settings.errors.save', 'An error occurred while saving.');
            setError(errorMessage);
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleFitnessAssessmentSubmit = async (answers) => {
        setIsSubmittingAssessment(true);
        setError('');
        setFeedbackMessage('');
        try {
            const response = await submitFitnessAssessment({
                answers,
                safetyAcknowledged: true
            });
            setFitnessAssessment(response.data?.fitnessAssessment || null);
            if (response.data?.user) {
                updateUser(response.data.user);
            }
            setFeedbackMessage(response.data?.message || t('settings.assessment.submittedFeedback', 'Fitness assessment submitted.'));
            refreshGenerationStatus();
            refreshAiFeatureQuotas();
        } catch (err) {
            setError(err.response?.data?.message || t('settings.assessment.submitFailed', 'Failed to submit fitness assessment.'));
        } finally {
            setIsSubmittingAssessment(false);
        }
    };

    const handlePreferredLocaleSave = async () => {
        setIsSavingLocale(true);
        setError('');
        setFeedbackMessage('');
        setWarningMessage('');
        console.info('[Locale][WebSettingsSaveStart]', {
            preferredLocale,
            username: formData.username || null,
        });
        try {
            const response = await saveUserProfile({
                username: formData.username,
                email: formData.email,
                profileData,
                preferredLocale,
                suppressQuestRegeneration: true
            });
            const latestProfile = await getUserProfile();
            populateForm(latestProfile.data);
            updateUser(latestProfile.data);
            console.info('[Locale][WebSettingsSaveSuccess]', {
                preferredLocale: latestProfile.data?.preferredLocale || null,
            });
            setFeedbackMessage(response.data?.message || t('settings.appLanguage.updated', 'Language preference updated.'));
        } catch (err) {
            console.info('[Locale][WebSettingsSaveError]', {
                preferredLocale,
                message: err.response?.data?.msg || err.response?.data?.message || err.message,
            });
            setError(err.response?.data?.msg || err.response?.data?.message || t('settings.appLanguage.error', 'Failed to update language preference.'));
        } finally {
            setIsSavingLocale(false);
        }
    };

    const handleDownloadData = async () => {
        try {
            const res = await requestDataPortability();
            const dataStr = JSON.stringify(res.data, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = window.URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'my_forza_data.json');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setFeedbackMessage(t('settings.download.started', 'Data download started.'));
        } catch (err) { setError(t('settings.download.failed', 'Failed to download data.')); }
    };

    const handleDeleteAccount = async () => {
        const normalized = deleteConfirmationText.trim().toUpperCase();
        if (normalized !== 'DELETE') {
            setError(t('settings.delete.typeDelete', 'Type DELETE to confirm account deletion.'));
            return;
        }
        setIsDeletingAccount(true);
        setError('');
        try {
            await deleteAccount();
            logout();
            navigate('/login', { replace: true });
        } catch (err) {
            setError(t('settings.delete.failed', 'Failed to delete account. Please try again.'));
        } finally {
            setIsDeletingAccount(false);
            setOpenDeleteConfirm(false);
            setDeleteConfirmationText('');
        }
    };

    useEffect(() => {
        if (generationStatus.state === 'running') {
            if (!statusPollRef.current) {
                statusPollRef.current = setInterval(() => {
                    refreshGenerationStatus();
                }, 5000);
            }
        } else if (statusPollRef.current) {
            clearInterval(statusPollRef.current);
            statusPollRef.current = null;
        }
    }, [generationStatus.state, refreshGenerationStatus]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (statusPollRef.current) {
                clearInterval(statusPollRef.current);
                statusPollRef.current = null;
            }
        };
    }, []);

    const isReschedulingInProgress = generationStatus.state === 'running';
    const activeJob = isReschedulingInProgress ? generationStatus.job : null;
    const generationFailed = generationStatus.state === 'failed';
    const failedJob = generationFailed ? generationStatus.job : null;
    const formattedStartTime = activeJob?.startedAt ? new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(activeJob.startedAt)) : null;
    const formattedTriggerSource = activeJob ? formatTriggerSource(activeJob.triggerSource) : null;
    const plannerQuota = getFeatureQuota(aiFeatureQuotas, AI_FEATURE_KEYS.QUESTS_PLANNER_RESCHEDULER);
    const plannerQuotaBlocked = isQuotaBlocked(plannerQuota);
    const plannerQuotaMessage = getQuotaMessage(plannerQuota);
    const plannerRewardedUnlock = getRewardedUnlock(plannerQuota);
    const canStartPlannerRewardedUnlock = Boolean(plannerRewardedUnlock?.canStart);
    const canOfferPlannerRewardedUnlock = plannerQuotaBlocked && user?.subscription?.status !== 'ultra';
    const plannerUnlockCopy = buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.QUEST_RESCHEDULE, plannerRewardedUnlock, t);
    useEffect(() => {
        const handleQuotaRefresh = () => {
            setAiFeatureQuotas(prev => {
                const planner = prev?.features?.[AI_FEATURE_KEYS.QUESTS_PLANNER_RESCHEDULER];
                if (!planner || !plannerQuotaBlocked || !canOfferPlannerRewardedUnlock) {
                    return prev;
                }
                return {
                    ...prev,
                    features: {
                        ...prev.features,
                        [AI_FEATURE_KEYS.QUESTS_PLANNER_RESCHEDULER]: unlockQuestRescheduleQuota(planner)
                    }
                };
            });
            setWarningMessage('');
            refreshAiFeatureQuotas();
        };
        window.addEventListener('forza:aiFeatureQuotasRefresh', handleQuotaRefresh);
        return () => window.removeEventListener('forza:aiFeatureQuotasRefresh', handleQuotaRefresh);
    }, [canOfferPlannerRewardedUnlock, plannerQuotaBlocked, refreshAiFeatureQuotas]);
    const rescheduleDisabled = isLoading || isReschedulingInProgress;
    const rescheduleTooltip = plannerQuotaBlocked
        ? plannerQuotaMessage
        : isReschedulingInProgress ? t('settings.plan.alreadyRunning', 'Quest plan regeneration is already running.') : undefined;
    const failureMessage = failedJob?.errorMessage || t('settings.plan.failedDefault', 'Quest regeneration did not complete. Please retry when ready.');
    const failureCompletedAt = failedJob?.finishedAt ? new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(failedJob.finishedAt)) : null;

    const displayName = resolveDisplayName({
        username: formData.username || user?.username || '',
        email: formData.email || user?.email || '',
        profileData
    });
    const displayEmail = formData.email || user?.email || '';
    const avatarUrl = user?.avatarUrl || null;
    const initials =
        displayName
            .split(' ')
            .filter(Boolean)
            .map(word => word[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() || 'FM';
    const deleteReady = deleteConfirmationText.trim().toUpperCase() === 'DELETE';
    const profileWorkoutDayValidation = validateWorkoutDaySelection(draftProfileData);
    const editorModeMeta = getProfileEditorModeMeta(profileEditorMode, t);
    const isEditingProfile = editorModeMeta.isEditing;
    const currentPlanLabel = getSubscriptionPlanLabel(user?.subscription?.status);
    const normalizedProfile = hydrateProfile(profileData);
    const preferredUnits = normalizedProfile.equipmentAndPreferences.preferredUnits || 'metric';
    const lengthUnit = measurementUnitShort(preferredUnits);
    const weightUnit = weightUnitLabel(preferredUnits);
    const heightValue = normalizedProfile.personalInformation.heightCm
        ? `${getLengthDisplayValue(normalizedProfile.personalInformation.heightCm, preferredUnits, 1, 0)} ${lengthUnit}`
        : null;
    const weightValue = normalizedProfile.personalInformation.weightKg
        ? `${getWeightDisplayValue(normalizedProfile.personalInformation.weightKg, preferredUnits)} ${weightUnit}`
        : null;
    const selectedUnit = UNIT_OPTIONS.find((option) => option.value === preferredUnits)?.label || t('settings.metricUnits', 'Metric (kg, km)');
    const snapshotRows = [
        {
            icon: UserRound,
            label: t('settings.labels.fullName', 'Full name'),
            value: formatValue(normalizedProfile.personalInformation.fullName, '')
        },
        {
            icon: CalendarRange,
            label: t('settings.labels.age', 'Age'),
            value: formatValue(normalizedProfile.personalInformation.age, '')
        },
        {
            icon: UserRound,
            label: t('settings.labels.gender', 'Gender'),
            value: formatValue(normalizedProfile.personalInformation.sex, '')
        },
        {
            icon: MapPin,
            label: t('settings.labels.location', 'Location'),
            value: formatValue(normalizedProfile.personalInformation.location, '')
        },
        {
            icon: Ruler,
            label: t('settings.labels.height', 'Height'),
            value: formatValue(heightValue, '')
        },
        {
            icon: Weight,
            label: t('settings.labels.weight', 'Weight'),
            value: formatValue(weightValue, '')
        }
    ].filter((item) => item.value && item.value !== 'Not provided');

    const trainingValue = [
        formatValue(normalizedProfile.healthAndFitnessDetails.exerciseFamiliarity, ''),
        normalizedProfile.healthAndFitnessDetails.weeklyWorkoutTarget
            ? t('settings.training.sessionsPerWeekValue', '{count} sessions / week', {
                count: normalizedProfile.healthAndFitnessDetails.weeklyWorkoutTarget
            })
            : '',
        weekdayLabels(normalizedProfile.healthAndFitnessDetails.preferredWorkoutDays) !== 'Not provided'
            ? weekdayLabels(normalizedProfile.healthAndFitnessDetails.preferredWorkoutDays)
            : ''
    ].filter(Boolean).join(' · ');

    const nutritionValue = [
        formatValue(normalizedProfile.additionalInformation.dietaryPreferences, ''),
        normalizedProfile.additionalInformation.allergies && normalizedProfile.additionalInformation.allergies !== 'None'
            ? t('settings.nutrition.allergiesValue', 'Allergies: {value}', {
                value: normalizedProfile.additionalInformation.allergies
            })
            : ''
    ].filter(Boolean).join(' · ');

    const assessmentSubmittedAt = fitnessAssessment?.submittedAt
        ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(fitnessAssessment.submittedAt))
        : null;
    const assessmentAssignedLevel = fitnessAssessment?.appliedLevel || fitnessAssessment?.assignedLevel || null;
    const hasCompletedAssessment = Boolean(fitnessAssessment?.submittedAt);
    let questStatusLabel = t('settings.planIdle', 'Plan idle');
    let questStatusTone = 'neutral';

    switch (generationStatus.state) {
        case 'running':
            questStatusLabel = t('settings.plan.rescheduling', 'Rescheduling...');
            questStatusTone = 'info';
            break;
        case 'failed':
            questStatusLabel = t('settings.plan.rescheduleFailed', 'Reschedule failed');
            questStatusTone = 'danger';
            break;
        case 'completed':
        case 'success':
        case 'succeeded':
            questStatusLabel = t('settings.plan.rescheduleComplete', 'Reschedule complete');
            questStatusTone = 'success';
            break;
        default:
            break;
    }

    const handleQuestRescheduleRewardedUnlock = () => {
        const showRewardedAd = window?.forzaNative?.showRewardedAd;
        if (typeof showRewardedAd !== 'function') {
            setWarningMessage(plannerUnlockCopy.fallbackMessage);
            return;
        }
        showRewardedAd('questRescheduleExtraUse');
    };

    return (
        <div
            ref={pageTopRef}
            className="min-h-screen pb-20 font-sans text-gray-300"
        >
            <main className="mx-auto flex w-full max-w-md flex-col gap-5 px-4 sm:px-5">
                <h1 className="my-4 text-[2.1rem] font-bold tracking-tight text-white">{t('settings.title', 'Settings')}</h1>

                <section className="flex flex-col gap-4">
                    {!hasProfileData && (
                        <div className="rounded-[1.5rem] border border-blue-500/20 bg-blue-950/25 p-4 text-sm text-blue-100 shadow-inner">
                            <p className="font-semibold text-blue-100">{t('settings.emptyState.title', 'We couldn’t detect any saved profile data.')}</p>
                            <p className="mt-1 text-blue-200">
                                {t('settings.emptyState.body', 'Run the onboarding assistant so quest scheduling has the inputs it needs.')}
                            </p>
                        </div>
                    )}

                    {isReschedulingInProgress && (
                        <div className="rounded-[1.5rem] border border-cyan-500/20 bg-cyan-950/30 p-4 text-sm text-cyan-100 shadow-inner">
                            <p className="font-semibold text-cyan-100">{t('settings.plan.inProgressTitle', 'Quest plan regeneration is in progress.')}</p>
                            <p className="mt-1 text-cyan-100/90">
                                {formattedTriggerSource
                                    ? t('settings.plan.triggeredBy', 'Triggered by {source}', { source: formattedTriggerSource })
                                    : t('settings.plan.triggeredBySystem', 'Triggered by system')}
                                {formattedStartTime ? ` · ${t('settings.plan.startedAt', 'Started at {time}', { time: formattedStartTime })}` : ''}. {t('settings.plan.autoUpdate', 'The dashboard will update automatically.')}
                            </p>
                        </div>
                    )}

                    {generationFailed && (
                        <div className="rounded-[1.5rem] border border-red-500/20 bg-red-950/35 p-4 text-sm text-red-100 shadow-inner">
                            <p className="font-semibold text-red-100">{t('settings.plan.failedTitle', 'Quest plan regeneration failed.')}</p>
                            <p className="mt-1 text-red-100/90">{failureMessage}</p>
                            {failureCompletedAt && (
                                <p className="mt-2 text-xs text-red-200">{t('settings.plan.lastAttempt', 'Last attempt finished at {time}. Retry when ready.', { time: failureCompletedAt })}</p>
                            )}
                        </div>
                    )}

                    {plannerQuotaBlocked && (
                        <div className="rounded-[1.5rem] border border-amber-500/20 bg-amber-950/35 p-4 text-sm text-amber-100 shadow-inner">
                            <p>{canStartPlannerRewardedUnlock ? plannerUnlockCopy.headline : plannerQuotaMessage}</p>
                            {canOfferPlannerRewardedUnlock && canStartPlannerRewardedUnlock && (
                                <button
                                    type="button"
                                    onClick={handleQuestRescheduleRewardedUnlock}
                                    className="mt-3 inline-flex items-center justify-center rounded-xl border border-amber-300/50 bg-amber-300/10 px-4 py-2 text-sm font-semibold text-amber-100 transition-colors hover:border-amber-200 hover:bg-amber-300/20"
                                >
                                    {plannerUnlockCopy.buttonLabel}
                                </button>
                            )}
                        </div>
                    )}

                    {feedbackMessage && (
                        <div className="rounded-[1.5rem] border border-emerald-500/20 bg-emerald-950/30 p-4 text-sm text-emerald-100 shadow-inner">
                            {feedbackMessage}
                        </div>
                    )}

                    {warningMessage && !plannerQuotaBlocked && (
                        <div className="rounded-[1.5rem] border border-amber-500/20 bg-amber-950/35 p-4 text-sm text-amber-100 shadow-inner">
                            {warningMessage}
                        </div>
                    )}

                    {error && (
                        <div className="rounded-[1.5rem] border border-red-500/20 bg-red-950/35 p-4 text-sm text-red-100 shadow-inner">
                            {error}
                        </div>
                    )}
                </section>

                <SurfaceCard>
                    <div className="relative overflow-hidden px-5 py-5">
                        <div className="absolute -right-14 -top-20 h-44 w-44 rounded-full bg-[#123d64]/70 blur-sm" />
                        <div className="absolute -bottom-20 -left-14 h-40 w-40 rounded-full bg-[#0d3140]/70 blur-sm" />
                        <div className="relative flex items-center gap-4">
                            <div className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#1c2638] text-3xl font-semibold text-gray-100">
                                {avatarUrl && !avatarFailed ? (
                                    <img
                                        src={avatarUrl}
                                        alt={`${displayName} avatar`}
                                        className="h-full w-full object-cover"
                                        referrerPolicy="no-referrer"
                                        onError={() => setAvatarFailed(true)}
                                    />
                                ) : (
                                    initials
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[1.7rem] font-bold leading-8 tracking-tight text-white">{displayName}</p>
                                {displayEmail && <p className="mt-1 truncate text-base text-[#96a7c0]">{displayEmail}</p>}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <StatusPill tone={questStatusTone}>{questStatusLabel}</StatusPill>
                                    <StatusPill tone="info">{currentPlanLabel}</StatusPill>
                                    <StatusPill tone="neutral">{t('settings.levelBadge', 'Level {level}', { level: Number(user?.level) || 1 })}</StatusPill>
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate('/subscription')}
                            className="relative mt-4 inline-flex w-full items-center justify-center gap-3 rounded-[1rem] border border-cyan-500/40 bg-[#0d2239] px-5 py-3 text-sm font-semibold text-cyan-200 transition-all duration-200 hover:border-cyan-300 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                        >
                            {t('settings.managePlan', 'Manage plan')}
                        </button>
                    </div>
                </SurfaceCard>

                <div className="space-y-2">
                    <SectionLabel>{t('settings.account', 'Account')}</SectionLabel>
                    <SurfaceCard>
                    <QuickActionCard
                        icon={CalendarRange}
                        title={t('settings.actions.rescheduleTitle', 'Reschedule Quests')}
                        description={t('settings.actions.rescheduleDescription', 'Adjust your profile inputs and start a fresh quest plan when you are ready.')}
                        onClick={handleQuestReschedule}
                        disabled={rescheduleDisabled}
                        locked={plannerQuotaBlocked}
                        loading={isLoading}
                    />
                    <div className="h-px bg-[#24344b]" />
                    <QuickActionCard
                        icon={PencilLine}
                        title={isEditingProfile ? t('settings.actions.editingProfile', 'Editing Profile') : t('settings.actions.editProfile', 'Edit Profile')}
                        description={t('settings.actions.editProfileDescription', 'Update your details and preferences without regenerating quests automatically.')}
                        onClick={enterProfileEditMode}
                        disabled={isEditingProfile}
                        tone="cyan"
                    />
                    </SurfaceCard>
                </div>

                {hasCompletedAssessment ? (
                    <div className="space-y-2">
                        <SectionLabel>{t('settings.assessment.title', 'Fitness Assessment')}</SectionLabel>
                        <SurfaceCard>
                        <div className="flex items-center gap-4 px-4 py-4">
                            <IconBadge icon={Check} success />
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-3">
                                    <h2 className="text-base font-semibold tracking-tight text-white">{t('settings.assessment.title', 'Fitness Assessment')}</h2>
                                    <StatusPill tone="success">{t('settings.assessment.completed', 'Completed')}</StatusPill>
                                </div>
                                <div className="mt-3 grid grid-cols-2">
                                    {assessmentAssignedLevel && (
                                        <div className="border-r border-[#24344b] pr-3">
                                            <p className="text-xs text-[#8fa1bb]">{t('settings.assessment.assignedLevel', 'Assigned level')}</p>
                                            <p className="mt-1 text-2xl font-bold text-white">{assessmentAssignedLevel}</p>
                                        </div>
                                    )}
                                    {assessmentSubmittedAt && (
                                        <div className="pl-4">
                                            <p className="text-xs text-[#8fa1bb]">{t('settings.assessment.submitted', 'Submitted')}</p>
                                            <p className="mt-1 text-lg font-semibold text-white">{assessmentSubmittedAt}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        </SurfaceCard>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <SectionLabel>{t('settings.assessment.title', 'Fitness Assessment')}</SectionLabel>
                        <FitnessAssessmentSettingsCard
                            fitnessAssessment={fitnessAssessment}
                            currentLevel={Number(user?.level) || 1}
                            onSubmit={handleFitnessAssessmentSubmit}
                            isSubmitting={isSubmittingAssessment}
                            disabled={isReschedulingInProgress}
                            locale={locale}
                            t={t}
                        />
                    </div>
                )}

                {snapshotRows.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-3 px-4">
                            <SectionLabel>{t('settings.profileSnapshot', 'Profile Snapshot')}</SectionLabel>
                            <StatusPill tone="neutral">{selectedUnit}</StatusPill>
                        </div>
                        <SurfaceCard>
                        <div className="grid grid-cols-2">
                            {snapshotRows.map((item, index) => (
                                <SnapshotTile
                                    key={item.label}
                                    icon={item.icon}
                                    label={item.label}
                                    value={item.value}
                                    index={index}
                                />
                            ))}
                        </div>
                        </SurfaceCard>
                    </div>
                )}

                {(trainingValue || nutritionValue) && (
                    <div className="space-y-2">
                        <SectionLabel>{t('settings.preferencesSection', 'Preferences')}</SectionLabel>
                        <SurfaceCard>
                        <div>
                            {trainingValue && (
                                <SettingRow
                                    icon={Dumbbell}
                                    title={t('settings.trainingSection', 'Training')}
                                    description={trainingValue}
                                    onClick={enterTrainingEditMode}
                                    disabled={isEditingProfile}
                                />
                            )}
                            {trainingValue && nutritionValue && <div className="h-px bg-[#24344b]" />}
                            {nutritionValue && (
                                <SettingRow
                                    icon={Utensils}
                                    title={t('settings.nutritionSection', 'Nutrition')}
                                    description={nutritionValue}
                                    onClick={enterProfileEditMode}
                                    disabled={isEditingProfile}
                                />
                            )}
                        </div>
                        </SurfaceCard>
                    </div>
                )}

                {(isEditingProfile || !hasProfileData) && (
                    <SurfaceCard ref={profileEditorRef} className="p-5 sm:p-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <h2 className="text-[2rem] font-semibold tracking-tight text-white">{editorModeMeta.title}</h2>
                                    <StatusPill tone={editorModeMeta.badgeTone}>
                                        {editorModeMeta.badgeLabel}
                                    </StatusPill>
                                </div>
                                <p className="mt-2 text-sm leading-7 text-slate-400">
                                    {editorModeMeta.description}
                                </p>
                            </div>
                        </div>
                        <div className="mt-6">
                            {hasProfileData ? (
                                <ProfileDataEditor
                                    formState={draftProfileData}
                                    onSectionChange={handleProfileSectionChange}
                                    onCancel={handleProfileEditCancel}
                                    onSave={handleProfileEditSave}
                                    isSaving={isSavingProfile}
                                    validation={profileWorkoutDayValidation}
                                    mode={profileEditorMode || 'full'}
                                />
                            ) : (
                                <div className="rounded-[1.35rem] border border-blue-500/20 bg-blue-950/20 p-4 text-sm text-blue-100">
                                    {t('settings.completeOnboardingFirst', 'Complete onboarding first, then come back here to edit your saved profile.')}
                                </div>
                            )}
                        </div>
                    </SurfaceCard>
                )}

                {!isEditingProfile && hasProfileData && (
                    <details className="group rounded-[2rem] border border-white/10 bg-[#0f1728]/80 p-5 shadow-2xl backdrop-blur-xl sm:p-6">
                        <summary className="cursor-pointer list-none text-lg font-semibold text-white marker:hidden">
                            <span className="flex items-center justify-between gap-4">
                                <span>{t('settings.fullExtractedProfile', 'Full Extracted Profile')}</span>
                                <ChevronRight size={20} className="text-slate-400 transition-transform duration-200 group-open:rotate-90" />
                            </span>
                        </summary>
                        <div className="mt-5">
                            <ProfileDataDisplay profile={profileData} t={t} />
                        </div>
                    </details>
                )}

                <div className="space-y-5">
                    <div className="space-y-2">
                        <SectionLabel>{t('settings.appSettings', 'App Settings')}</SectionLabel>
                        <SurfaceCard>
                        <div>
                            <div className="px-4 py-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-base font-semibold tracking-tight text-white">{t('settings.appLanguage.title', 'App Language')}</p>
                                        <p className="mt-1 text-sm text-[#8fa1bb]">{t('settings.appLanguage.description', 'Choose the default app and AI language. Legal pages remain in English.')}</p>
                                    </div>
                                    <StatusPill tone="neutral">{getLocaleLabel(preferredLocale)}</StatusPill>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                                    {SUPPORTED_LOCALES.map((localeOption) => (
                                        <button
                                            key={localeOption.code}
                                            type="button"
                                            onClick={() => setPreferredLocale(localeOption.code)}
                                            className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                                                preferredLocale === localeOption.code
                                                    ? 'border-cyan-400 bg-cyan-400/20 text-cyan-100'
                                                    : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-500/50'
                                            }`}
                                        >
                                            {localeOption.label}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={handlePreferredLocaleSave}
                                    disabled={isSavingLocale}
                                    className="mt-4 inline-flex items-center justify-center rounded-2xl border border-cyan-500/40 bg-[#0d2239] px-4 py-3 text-sm font-semibold text-cyan-200 transition-all duration-200 hover:border-cyan-300 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isSavingLocale ? t('settings.appLanguage.saving', 'Saving…') : t('settings.appLanguage.save', 'Save Language')}
                                </button>
                            </div>
                            <div className="h-px bg-[#24344b]" />
                            <SettingRow
                                icon={Bell}
                                title={t('settings.push.title', 'Push Notifications')}
                                description={getPushNotificationsDescription()}
                                onClick={handlePushNotificationsPress}
                                disabled={isPushNotificationsPending}
                            />
                            {showPushSettingsButton && (
                                <div className="px-4 pb-4">
                                    <button
                                        type="button"
                                        onClick={handleOpenNativeSettings}
                                        className="rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-200"
                                    >
                                        {t('settings.push.openSettings', 'Open iPhone Settings')}
                                    </button>
                                </div>
                            )}
                            {isNativeApp && (
                                <div className="px-4 pb-4">
                                    <button
                                        type="button"
                                        onClick={handleSendTestPush}
                                        disabled={isPushNotificationsPending || isPushTestPending}
                                        className="rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-200 transition-colors duration-200 hover:border-cyan-300 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isPushTestPending
                                            ? t('settings.push.sendingTest', 'Sending test push…')
                                            : t('settings.push.sendTest', 'Send Test Push')}
                                    </button>
                                    <p className="mt-3 text-xs leading-5 text-slate-400">
                                        {t('settings.push.testDescription', 'Sends a one-off notification to this iPhone and reports the current Expo delivery state.')}
                                    </p>
                                    {pushTestDiagnostic && (
                                        <div className={`mt-3 rounded-2xl border px-4 py-3 text-sm ${pushTestStatusToneClassName}`}>
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <p className="font-semibold">{t('settings.push.lastTest', 'Last test result')}</p>
                                                <span className="rounded-full border border-current/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide">
                                                    {pushTestStatusLabel}
                                                </span>
                                            </div>
                                            {pushTestDiagnostic.message && (
                                                <p className="mt-2 text-sm opacity-90">{pushTestDiagnostic.message}</p>
                                            )}
                                            {pushTestDiagnostic.ticketId && (
                                                <p className="mt-2 break-all text-xs opacity-80">
                                                    {t('settings.push.ticketId', 'Ticket ID')}: {pushTestDiagnostic.ticketId}
                                                </p>
                                            )}
                                            {pushTestReceiptError && (
                                                <p className="mt-1 break-all text-xs opacity-80">
                                                    {t('settings.push.providerError', 'Provider error')}: {pushTestReceiptError}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {isNativeApp && (
                                <>
                                    <div className="h-px bg-[#24344b]" />
                                    <SettingRow
                                        icon={HeartPulse}
                                        title={t('settings.healthKit.title', 'HealthKit Verification')}
                                        description={t('settings.healthKit.description', 'Manage iOS HealthKit access for steps, sleep, and active-calorie quest verification.')}
                                        onClick={handleOpenHealthKitSettings}
                                    />
                                </>
                            )}
                            <div className="h-px bg-[#24344b]" />
                            <SettingRow
                                icon={BookOpen}
                                title={t('settings.guideRestore.title', 'Restore Feature Guide')}
                                description={t('settings.guideRestore.description', 'Reopen the dashboard walkthrough and product tips.')}
                                onClick={handleResetGuide}
                            >
                                {showFeedback && (
                                    <div className="mt-3 flex items-center gap-3 rounded-2xl border border-teal-500/30 bg-teal-500/10 px-3 py-3 text-teal-100">
                                        <div className="rounded-full bg-teal-500/20 p-2 text-teal-300">
                                            <Check size={16} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-teal-300">{t('settings.guideRestore.successTitle', 'Success')}</p>
                                            <p className="text-xs text-teal-100/80">{t('settings.guideRestore.successBody', 'Feature guide restored. Check your Dashboard.')}</p>
                                        </div>
                                    </div>
                                )}
                            </SettingRow>
                            <div className="h-px bg-[#24344b]" />
                            <SettingRow
                                icon={UserRound}
                                title={t('settings.aboutForza.title', 'About Forza')}
                                description={t('settings.aboutForza.description', 'Open the public guide, product overview, and launch information.')}
                                onClick={() => navigate('/about')}
                            />
                            <div className="h-px bg-[#24344b]" />
                            <SettingRow
                                icon={BookOpen}
                                title={t('settings.healthSources.title', 'Health Sources')}
                                description={t('settings.healthSources.description', 'See where Forza health tips and planning defaults come from, with links to original sources.')}
                                onClick={() => navigate('/health-sources')}
                            />
                        </div>
                        </SurfaceCard>
                    </div>

                    <div className="space-y-2">
                        <SectionLabel>{t('settings.dataPrivacy', 'Data & Privacy')}</SectionLabel>
                        <SurfaceCard>
                        <div>
                            <SettingRow
                                icon={Download}
                                title={t('settings.download.title', 'Download My Data')}
                                description={t('settings.download.description', 'Export your profile, settings, and quest history.')}
                                onClick={handleDownloadData}
                            />
                            <div className="h-px bg-[#24344b]" />
                            <SettingRow
                                icon={Headset}
                                title={t('settings.support.title', 'Support')}
                                description={t('settings.support.description', 'Get help and contact us directly.')}
                                onClick={() => navigate('/support')}
                            />
                            <div className="h-px bg-[#24344b]" />
                            <SettingRow
                                icon={LogOut}
                                title={t('settings.logout.title', 'Logout')}
                                description={t('settings.logout.description', 'Sign out of your account on this device.')}
                                onClick={handleLogout}
                            />
                        </div>
                        </SurfaceCard>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => setOpenDeleteConfirm(true)}
                    className="w-full text-left"
                >
                    <div className="rounded-[1.5rem] border border-red-500/50 bg-red-950/20 shadow-2xl transition-colors duration-200 hover:border-red-400/70">
                        <div className="flex items-center gap-4">
                            <div className="px-4 py-4">
                                <IconBadge icon={Trash2} destructive />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-base font-semibold tracking-tight text-red-300">{t('settings.delete.title', 'Delete Account')}</p>
                                <p className="mt-1 text-sm leading-5 text-red-100/85">
                                    {t('settings.delete.description', 'Permanently delete your account and anonymize all linked data.')}
                                </p>
                            </div>
                            <div className="pr-4">
                            <ChevronRight size={22} className="text-red-300" strokeWidth={1.8} />
                            </div>
                        </div>
                    </div>
                </button>
                <p className="px-4 text-sm leading-6 text-slate-500">
                    {t('settings.delete.footnote', 'Deleting your account anonymizes your profile and removes HealthKit snapshots. Audit logs remain anonymized for up to 12 months.')}
                </p>
            </main>
            <Modal
                isOpen={openDeleteConfirm}
                onClose={() => {
                    setOpenDeleteConfirm(false);
                    setDeleteConfirmationText('');
                }}
                title={t('settings.delete.modalTitle', 'Delete your account')}
            >
                <div className="space-y-4 text-sm text-gray-300">
                    <p>
                        {t('settings.delete.modalBody', 'This will permanently delete your account. Your email, username, and profile data will be anonymized. HealthKit snapshots are removed. Anonymized logs are retained for up to 12 months for audit and fraud prevention.')}
                    </p>
                    <div>
                        <label htmlFor="delete-confirm" className="block text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {t('settings.delete.confirmLabel', 'Type DELETE to confirm')}
                        </label>
                        <input
                            id="delete-confirm"
                            type="text"
                            value={deleteConfirmationText}
                            onChange={(e) => setDeleteConfirmationText(e.target.value)}
                            className="mt-2 w-full rounded-lg border border-gray-700 bg-[#1E1E1E] px-3 py-2 text-sm text-white focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                            placeholder={t('settings.delete.confirmPlaceholder', 'DELETE')}
                        />
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <button
                            type="button"
                            onClick={() => {
                                setOpenDeleteConfirm(false);
                                setDeleteConfirmationText('');
                            }}
                            className="inline-flex flex-1 items-center justify-center rounded-full border border-[#3d4349] bg-[#2C2F33] px-4 py-2 text-sm font-medium text-gray-200 transition-colors duration-200 hover:bg-[#34383D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400"
                        >
                            {t('settings.delete.cancel', 'Cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleDeleteAccount}
                            disabled={!deleteReady || isDeletingAccount}
                            className={`inline-flex flex-1 items-center justify-center rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 ${deleteReady && !isDeletingAccount
                                ? 'bg-red-600 hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400'
                                : 'cursor-not-allowed bg-red-900/40 text-red-200'
                                }`}
                        >
                            {isDeletingAccount ? t('settings.delete.deleting', 'Deleting...') : t('settings.delete.confirm', 'Delete Account')}
                        </button>
                    </div>
                </div>
            </Modal>
        </div >
    );
};

export default SettingsPage;
